import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { QuestionPayload, Question } from '../../schema';
import { createQuestionAtom, listLoadQuestionAtom } from '../../atoms/question';
import {
  notificationAtom,
  NotificationType,
  pageLoadingAtom,
} from '../../atoms/system';
import { useAtom, useSetAtom } from 'jotai';
import { CommentStatus } from '../../types';
import api from '../../api';
import FormQuestion from './Form.Question';
import cuid from 'cuid';

const Create = (): JSX.Element => {
  const notification = useSetAtom(notificationAtom);
  const pageLoading = useSetAtom(pageLoadingAtom);
  const setListLoad = useSetAtom(listLoadQuestionAtom);
  const [showCreateQuestion, setShowCreateQuestion] =
    useAtom(createQuestionAtom);
  const form = useForm<QuestionPayload>({
    resolver: zodResolver(Question),
    defaultValues: {
      target_id: '',
      status: CommentStatus.pending,
      desc: '',
      name: '',
      email: '',
      phone: '',
    },
  });
  const onSubmit = async (data: QuestionPayload) => {
    try {
      pageLoading(true);
      await api.post('api/questions', {
        json: data,
      });
      setListLoad(cuid());
      setShowCreateQuestion(false);
      notification({
        show: true,
        type: NotificationType.success,
        title: 'Tạo mới thành công',
      });
    } catch (e) {
      console.log(e);
      notification({
        show: true,
        type: null,
        title: null,
      });
    } finally {
      pageLoading(false);
    }
  };
  return (
    <>
      <Transition show={showCreateQuestion} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[999]"
          onClose={() => setShowCreateQuestion(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden p-4 rounded-xl bg-white shadow-xl transition-all">
                  <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                      <div className="w-full">
                        <h3 className="text-lg font-medium text-gray-900">
                          Tạo đánh giá mới
                        </h3>
                        <FormQuestion />
                        <div className="flex justify-between w-full mt-6">
                          <button
                            onClick={() => {
                              setShowCreateQuestion(false);
                            }}
                            type="button"
                            className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          >
                            Hủy
                          </button>
                          <button
                            type="submit"
                            className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Tạo câu hỏi
                          </button>
                        </div>
                      </div>
                    </form>
                  </FormProvider>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default Create;
